/**
 * InlinePickerPopover — pick or create, without leaving the row.
 *
 * THE SHEET-INSIDE-A-SHEET IS THE PATTERN THIS KILLS. Picking a witness meant opening a popover,
 * finding nobody, opening a right-hand sheet ON TOP of the sheet already holding the form, filling
 * it in, and landing back somewhere the eye had lost. The two-panel popover is the same two jobs
 * with none of the travel: a search-first list, and — always at the foot, always the last row —
 * `+ Create "<what you typed>"`, which opens a create panel inside the same surface, seeded with
 * the text that just failed to match.
 *
 * WHY THE CREATE ROW IS ALWAYS THERE. An empty result is the exact moment a picker is most likely
 * to be holding a person who genuinely does not exist yet, and it is the moment every list-only
 * picker dead-ends. Keeping the row mounted costs one row and removes the dead end; keeping it LAST
 * costs nothing and keeps `⏎` meaning "take the top match" while there is one to take.
 *
 * GENERIC OVER WHAT IS BEING PICKED. Witnesses, insurers, trackers, unassigned transactions: the
 * caller supplies rows (`id` · `search` · `item`) and render props for the label and the meta, and
 * this file never learns what any of them are.
 *
 * KEYBOARD-COMPLETE, AND NOTHING IS HOVER-GATED. Tab reaches the trigger, `Enter`/`Space` opens it,
 * cmdk owns ↑ ↓ ⏎ inside the list, `Esc` closes the create panel first and the popover second. The
 * chevron, the meta on every row and the create row are drawn at rest — there is no affordance here
 * that appears only under a pointer.
 */

import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { createRowLabel, emptyListText, filterPickerRows, type PickerRow } from './pickerState';

export type { PickerRow } from './pickerState';

/**
 * The create panel's own field, and its action row.
 *
 * THEY ARE EXPORTED RATHER THAN LEFT TO THE CALLER because a `create.render` written in Tailwind
 * next to a popover written in `sd-` classes is two design languages inside one 320px surface —
 * and because Tailwind purges a component-layer rule whose class name appears in no source file,
 * so `sd-pop-f` existing only in `index.css` is `sd-pop-f` not existing at all.
 */
export function PickerPanelField({
    id,
    label,
    children,
}: {
    id: string;
    label: string;
    children: ReactNode;
}) {
    return (
        <div className="sd-pop-f">
            <label htmlFor={id}>{label}</label>
            {children}
        </div>
    );
}

export function PickerPanelActions({ children }: { children: ReactNode }) {
    return <div className="sd-pop-actions">{children}</div>;
}

/** The requirement sentence, in the voice a refusal takes once one has actually happened. */
export function PickerPanelNote({ bad, children }: { bad?: boolean; children: ReactNode }) {
    return <p className={cn('sd-pop-req', bad && 'sd-pop-req-bad')}>{children}</p>;
}

export interface InlinePickerCreate {
    /** The noun the unseeded create row names: `Create a new witness`. */
    noun: string;
    /** The standing requirement sentence above the fields, if the flow has one. */
    requirement?: ReactNode;
    /**
     * The create form. `typed` is what was in the search box, to seed the first field; `close`
     * dismisses the whole popover (call it once the new item is created AND selected); `cancel`
     * drops back to the list.
     */
    render: (args: { typed: string; close: () => void; cancel: () => void }) => ReactNode;
}

export interface InlinePickerPopoverProps<T> {
    /** The DOM id a manifest row's `jump.focus` names — it lands on the trigger. */
    id: string;
    /** The row's label, left column: `2 · Witness`. */
    label: string;
    rows: PickerRow<T>[];
    selectedId: string | null;
    /** What the trigger reads once something is picked. */
    selectedLabel: ReactNode;
    /**
     * The trigger's own tooltip, for the two cases where the visible label is not the whole fact.
     *
     * A LABEL DRAWN AS JSX HAS NO TEXT TO READ BACK — the vehicle row's `name · plate` sets the
     * plate in tabular figures — so its caller hands the same line over here as a string. A label
     * that is DELIBERATELY SHORTER than what the row knows does the same: the witness trigger
     * shows the name because one witness is picked, and hands over `name · phone · address`. Where
     * neither applies, a string label reads itself back and this can be left off.
     */
    selectedTitle?: string;
    /** …and while nothing is. */
    emptyLabel: string;
    /** `Search people, or type a new name`. */
    searchPlaceholder: string;
    /**
     * The typed text, for a list whose SERVER has to see it.
     *
     * MOST PICKERS DO NOT NEED THIS — `filterPickerRows` matches every term anywhere over whatever
     * the rows already hold. The transaction pool does: `get_unassigned_transactions` caps its
     * answer at fifty rows, so a payment older than the fiftieth can only be reached by a query
     * that reaches the RPC. Filtering still runs on what comes back; this only widens what comes.
     */
    onQueryChange?: (query: string) => void;
    /** The noun the empty list names — defaults to the create noun. */
    noun?: string;
    /**
     * The row's own content, and the only cell that gives way when the row runs out of width.
     *
     * A NAME IS THE USUAL CASE and needs nothing but a string. A row with more than one fact to
     * carry — a payment is an amount, a day and the bank's own text — returns its whole line here
     * and lays it out itself, which is the only way the fact that MATTERS keeps a column of its
     * own rather than being squeezed by whatever the bank wrote. See `MoneyRow`.
     */
    renderLabel: (item: T) => ReactNode;
    /**
     * The label IS an amount, so it may not shrink below its own text.
     *
     * MONEY NEVER TRUNCATES IN A PICKER. A label cell is `min-width: 0` like every other flex
     * item, which is exactly what lets it ellipse past its own content when the meta beside it
     * runs long — and `GH₵90,00…` and `GH₵90,000.00` read the same at a glance, which is the one
     * confusion this canvas cannot afford. Set it wherever `renderLabel` returns a plain string
     * carrying a figure; a row that lays out its own amount column (`MoneyRow`) does not need it.
     */
    moneyLabel?: boolean;
    /** The right-hand line on each row: `phone on file · address missing`. */
    renderMeta?: (item: T) => ReactNode;
    /**
     * The whole row as one string, for the row's own `title`.
     *
     * A ROW IS THE TRUNCATING UNIT, so the tooltip is the row's rather than any one cell's: the
     * label and the meta are laid out as two cells but they read as one line, and a pointer that
     * has to find the exact cell that happened to ellipse is a pointer that finds nothing. The
     * caller composes it because only the caller knows which facts the two render props drew —
     * `renderLabel` returns a node, and a node cannot be read back as text.
     */
    rowTitle?: (item: T) => string;
    /**
     * The surface's width in px.
     *
     * THE WIDTH IS THE LONGEST ROW THIS PICKER CAN ACTUALLY DRAW, measured, not guessed. A row
     * spends 70px on chrome (border, group padding, item padding, the 14px tick column, two 10px
     * gaps and the meta's 8px lead), and the remainder is the label plus the meta at 13px/11.5px
     * Inter — so the number below is that remainder plus what the longest realistic pair needs,
     * plus about 12px for a scrollbar. The five sizes in use, and what set each:
     *
     * - `320` — a name and a short meta. The baseline; nothing on this canvas is this narrow now.
     * - `380` — a tracker: a 15-digit serial (126px) and `SIM 0244123456` (98px).
     * - `500` — a two-fact row. `Insured at GH₵90,000.00` (159px) beside
     *   `Jan 8, 2026 — Jan 7, 2027 · 18d left` (202px) is the widest of the two that use it;
     *   a plate beside make-model-and-VIN sits under it.
     * - `560` — a person beside their own contact line, the widest pair here that is not bank
     *   text: a full Ghanaian name at 225px, `Emmanuel Kwabena Asante-Mensah`, beside the witness
     *   meta `0244 123 456 · 12 Osu Badu St, Accra` at 206px. The 418px 500 offers took a glyph
     *   off the NAME as well as off the street, because flex shrinkage is proportional and every
     *   cell that can give gives something.
     * - `600` — a row carrying bank text, which is the one string here with no ceiling. The
     *   transaction row spends 208px on its amount and date columns, so 600 leaves the
     *   description 328px — a clear majority of the line, and enough for
     *   `ATM WD GTB000000000079 ACC 0221 CHARGE` (285px) whole. The deposit pool's meta ends in
     *   the same bank text and takes the same width.
     *
     * The surface is capped at the space Radix says it actually has, so a wide picker near the
     * right edge of a 1280px window narrows instead of overflowing.
     */
    width?: number;
    onSelect: (row: PickerRow<T>) => void;
    /** Omit where the flow has no empty state — the row then cannot be unpicked here. */
    onClear?: () => void;
    clearLabel?: string;
    create?: InlinePickerCreate;
    /** The list is still loading. */
    loading?: boolean;
    disabled?: boolean;
    /** The quiet line beside the trigger: `picked from your people, or created here`. */
    hint?: ReactNode;
    /** The pick is an uncommitted local change — same 2px margin rule the quiet fields use. */
    dirty?: boolean;
    /** The server's own refusal for this row, verbatim. */
    error?: string | null;
}

export default function InlinePickerPopover<T>({
    id,
    label,
    rows,
    selectedId,
    selectedLabel,
    selectedTitle,
    emptyLabel,
    searchPlaceholder,
    onQueryChange,
    noun,
    renderLabel,
    moneyLabel,
    renderMeta,
    rowTitle,
    width = 320,
    onSelect,
    onClear,
    clearLabel = 'Clear selection',
    create,
    loading,
    disabled,
    hint,
    dirty,
    error,
}: InlinePickerPopoverProps<T>) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [creating, setCreating] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const listNoun = noun ?? create?.noun ?? 'match';

    /**
     * The panel takes the caret the moment it opens, the way the sheet it replaces did. The caller
     * owns the fields, so this reaches for the first focusable one rather than being handed a ref.
     */
    useEffect(() => {
        if (!creating) return;
        const first = panelRef.current?.querySelector<HTMLElement>(
            'input, textarea, select, [contenteditable="true"]',
        );
        first?.focus();
    }, [creating]);

    function type(next: string) {
        setQuery(next);
        onQueryChange?.(next);
    }

    function reset() {
        type('');
        setCreating(false);
    }

    function change(next: boolean) {
        setOpen(next);
        if (!next) reset();
    }

    const visible = filterPickerRows(rows, query, selectedId);
    const triggerText = loading ? 'Loading…' : selectedId !== null ? selectedLabel : emptyLabel;
    /*
     * THE HANDED-OVER TITLE WINS WHERE THERE IS ONE, and only then does a string label read itself
     * back. A caller that supplies `selectedTitle` beside a text label means it — the label is the
     * identity and the title is the whole line — and it applies only while something IS picked:
     * `Loading…` and `No witness chosen` have nothing more to say than what they already say.
     */
    const held = !loading && selectedId !== null;
    const triggerTitle =
        (held ? selectedTitle : undefined) ??
        (typeof triggerText === 'string' ? triggerText : undefined);

    return (
        <div className={cn('sd-fld', dirty && 'sd-dirty', error && 'sd-errored')}>
            <label className="sd-fld-k" htmlFor={id}>
                {label}
            </label>
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                <Popover open={open} onOpenChange={change}>
                    <PopoverTrigger asChild>
                        <button
                            id={id}
                            type="button"
                            className="sd-pick"
                            aria-haspopup="dialog"
                            aria-expanded={open}
                            aria-invalid={error ? true : undefined}
                            aria-errormessage={error ? `${id}-err` : undefined}
                            disabled={disabled || loading}
                        >
                            {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />}
                            {/* THE TOOLTIP GOES ON THE CELL THAT ELLIPSES, not on the button:
                                the chevron is not truncated text and a title spanning it would
                                fire over a control that has nothing more to say. */}
                            <span
                                className={cn(
                                    'sd-pick-v',
                                    selectedId === null && !loading && 'sd-pick-empty',
                                )}
                                title={triggerTitle}
                            >
                                {triggerText}
                            </span>
                            <ChevronsUpDown className="sd-pick-chev h-3.5 w-3.5 shrink-0" />
                        </button>
                    </PopoverTrigger>

                    {/*
                     * `sd-tokens` BECAUSE THIS LEAVES THE TREE. Radix portals popover content to
                     * `document.body`, where the custom properties defined on `.setup-dossier` no
                     * longer inherit — without it the tints, the easings and the elevation token
                     * all resolve to nothing and the surface renders as bare shadcn.
                     */}
                    <PopoverContent
                        /*
                         * THE WIDTH IS INLINE, NOT A UTILITY. It is a prop, so it cannot be a
                         * class name Tailwind can see — and an inline style is also the only
                         * declaration that reliably beats the `w-72` shadcn's own PopoverContent
                         * ships with, whatever order the two utilities land in the sheet.
                         */
                        style={{ width }}
                        className="sd-tokens sd-pop p-0"
                        align="start"
                        sideOffset={6}
                        onEscapeKeyDown={(event) => {
                            // Esc closes the create panel FIRST. Closing the whole popover from
                            // inside a half-typed create form throws the typing away silently.
                            if (creating) {
                                event.preventDefault();
                                setCreating(false);
                            }
                        }}
                    >
                        <Command shouldFilter={false} loop label={label}>
                            <CommandInput
                                value={query}
                                onValueChange={type}
                                placeholder={searchPlaceholder}
                                className="sd-pop-search"
                            />
                            <CommandList className={cn('sd-pop-list', creating && 'sd-pop-list-short')}>
                                {/* Unpicking does not depend on the search: a query that matches
                                    nobody must not also take away the way out of a wrong pick. */}
                                {onClear && selectedId !== null && (
                                    <CommandGroup className="sd-pop-group">
                                        <CommandItem
                                            value="r-clear"
                                            className="sd-pop-item sd-pop-item-quiet"
                                            onSelect={() => {
                                                onClear();
                                                change(false);
                                            }}
                                        >
                                            <span className="sd-pop-mark" />
                                            <span className="sd-pop-n">{clearLabel}</span>
                                        </CommandItem>
                                    </CommandGroup>
                                )}
                                {loading ? (
                                    <div className="sd-pop-note">Loading…</div>
                                ) : visible.length === 0 ? (
                                    <div className="sd-pop-note">
                                        {emptyListText(query, listNoun)}
                                    </div>
                                ) : (
                                    <CommandGroup className="sd-pop-group">
                                        {visible.map((row, index) => (
                                            <CommandItem
                                                key={row.id}
                                                value={`r${index}`}
                                                className="sd-pop-item"
                                                title={rowTitle?.(row.item)}
                                                onSelect={() => {
                                                    onSelect(row);
                                                    change(false);
                                                }}
                                            >
                                                <span className="sd-pop-mark">
                                                    {row.id === selectedId && (
                                                        <Check className="h-3.5 w-3.5" />
                                                    )}
                                                </span>
                                                <span
                                                    className={cn(
                                                        'sd-pop-n',
                                                        moneyLabel && 'sd-pop-n-money',
                                                    )}
                                                >
                                                    {renderLabel(row.item)}
                                                </span>
                                                {renderMeta && (
                                                    <span className="sd-pop-m">
                                                        {renderMeta(row.item)}
                                                    </span>
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                )}
                            </CommandList>

                            {create && (
                                <>
                                    <CommandSeparator className="sd-pop-sep" />
                                    <CommandGroup className="sd-pop-group">
                                        <CommandItem
                                            value="r-create"
                                            className="sd-pop-item sd-pop-item-create"
                                            onSelect={() => setCreating(true)}
                                        >
                                            <span className="sd-pop-mark">
                                                <Plus className="h-3.5 w-3.5" />
                                            </span>
                                            <span className="sd-pop-n">
                                                {createRowLabel(query, create.noun)}
                                            </span>
                                            <span className="sd-pop-kbd">⏎</span>
                                        </CommandItem>
                                    </CommandGroup>
                                </>
                            )}
                        </Command>

                        {/*
                         * OUTSIDE `<Command>`, DELIBERATELY. cmdk binds ↑ ↓ ⏎ on its root, and a
                         * form nested inside it loses arrow keys inside its own inputs.
                         */}
                        {create && creating && (
                            <div
                                className="sd-pop-panel"
                                role="group"
                                aria-label={`Create a new ${create.noun}`}
                                ref={panelRef}
                            >
                                {create.requirement && (
                                    <PickerPanelNote>{create.requirement}</PickerPanelNote>
                                )}
                                {create.render({
                                    typed: query.trim(),
                                    close: () => change(false),
                                    cancel: () => setCreating(false),
                                })}
                            </div>
                        )}
                    </PopoverContent>
                </Popover>

                {hint && <span className="sd-pick-hint">{hint}</span>}
            </div>
            {error && (
                <p className="sd-f-err" id={`${id}-err`}>
                    {error}
                </p>
            )}
        </div>
    );
}
